import { SelectOption } from '@consigli/facade';
import { BuildingPackageType, BuildingPackageTypeLabels, StatusVariant } from '@consigli/types';

import { Field, Fieldset, Select, TextInput } from '@/components';
import { FileUpload } from '@/components/file-upload';
import { RadioGroup } from '@/components/radio-group';

interface BuildingPackageFormProps {
  name: string;
  packageType: BuildingPackageType;
  inputType: 'files' | 'json';
  files: File[];
  errors?: Record<string, string>;
  onNameChange: (value: string) => void;
  onPackageTypeChange: (value: BuildingPackageType) => void;
  onInputTypeChange: (value: 'files' | 'json') => void;
  onFilesChange: (files: File[]) => void;
}

/**
 * Form for creating a building package.
 * @param name - The name of the building package.
 * @param packageType - The type of the building package.
 * @param inputType - The type of the input.
 * @param files - The files to upload.
 * @param errors - The errors of the building package.
 * @param onNameChange - The function to call when the name is changed.
 * @param onPackageTypeChange - The function to call when the package type is changed.
 * @param onInputTypeChange - The function to call when the input type is changed.
 * @param onFilesChange - The function to call when the files are changed.
 * @returns The building package form.
 */
export const BuildingPackageForm = ({
  name,
  packageType,
  inputType,
  files,
  errors = {},
  onNameChange,
  onPackageTypeChange,
  onInputTypeChange,
  onFilesChange,
}: BuildingPackageFormProps) => {
  const buildingPackageTypeOptions: SelectOption[] = Object.entries(BuildingPackageTypeLabels)
    // The Solution Viewer creates wastewater packages when it starts their extraction.
    .filter(([value]) => value !== BuildingPackageType.WASTEWATER_TREATMENT)
    .map(([value, label]) => ({
      label,
      value: value as BuildingPackageType,
    }));

  const inputTypeOptions: SelectOption[] = [
    { label: 'Upload Files', value: 'files' },
    { label: 'Building JSON File', value: 'json' },
  ];

  return (
    <Fieldset legend="Building Package">
      <p className="text-sm text-gray-500">
        Please fill in the details to add a new building package to the project.
      </p>
      <Field label="Name" htmlFor="name-input" required error={errors.name}>
        <TextInput
          placeholder="Please enter a package name"
          className="w-full"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
        />
      </Field>
      <Field label="Package Type" htmlFor="package-type-select" required error={errors.packageType}>
        <Select
          options={buildingPackageTypeOptions}
          placeholder="Select Package Type"
          className="w-full"
          value={buildingPackageTypeOptions.find((opt) => opt.value === packageType)}
          onValueChange={(option) => onPackageTypeChange(option.value as BuildingPackageType)}
          getOptionLabel={(option) => option.label}
          getOptionValue={(option) => String(option.value)}
        />
      </Field>
      <Field label="Input Type" htmlFor="input-type-select" required error={errors.inputType}>
        <RadioGroup
          options={inputTypeOptions}
          value={inputType}
          onChange={(value) => onInputTypeChange(value as 'files' | 'json')}
        />
      </Field>
      <Field
        label="Files"
        htmlFor="files-input"
        required={inputType === 'files'}
        error={errors.files}
      >
        <FileUpload
          setFiles={onFilesChange}
          files={files}
          alert={
            inputType === 'files'
              ? {
                  title: 'Necessary files',
                  description:
                    'This service requires an Architectural model (.ifc) and 3D model of technical equipment and components (.ifc)',
                  variant: StatusVariant.INFO,
                }
              : {
                  title: 'Necessary file',
                  description: 'This service requires a Building JSON file',
                  variant: StatusVariant.INFO,
                }
          }
          acceptedFileFormats={inputType === 'files' ? ['.ifc'] : ['.json']}
        />
      </Field>
    </Fieldset>
  );
};
